/**
 * Customer List Page
 * Paginated list of customers with a client-side name filter.
 *
 * The backend GET /customers endpoint has no search parameter, so the filter
 * only applies to the currently loaded page.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createAPIClient, ICustomersApi } from '../api';
import * as types from '../api/types';
import { getErrorMessage } from '../utils/apiError';
import { canManageCustomers } from '../utils/permissions';
import { CustomerStatusBadge } from '../components/CustomerStatusBadge';
import { Toast } from '../components/Toast';

const PAGE_SIZE = 20;

export function CustomerListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const apiClient = React.useMemo<ICustomersApi>(() => createAPIClient(), []);
  const canManage = canManageCustomers(user);

  // State
  const [customers, setCustomers] = useState<types.Customer[]>([]);
  const [pagination, setPagination] = useState<types.PaginationInfo | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load data
  useEffect(() => {
    const loadCustomers = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await apiClient.listCustomers(page, PAGE_SIZE);
        setCustomers(response.data);
        setPagination(response.pagination);
      } catch (err) {
        setError(getErrorMessage(err, 'Failed to load customers'));
      } finally {
        setIsLoading(false);
      }
    };

    loadCustomers();
  }, [apiClient, page]);

  const term = search.trim().toLowerCase();
  const visibleCustomers = term
    ? customers.filter((c) => c.name.toLowerCase().includes(term))
    : customers;

  const totalPages = Math.max(1, pagination?.pages ?? 1);

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Title */}
      <div className="flex flex-wrap justify-between items-center gap-2 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
        {canManage && (
          <button
            onClick={() => navigate('/customers/new')}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 sm:py-2 px-4 rounded-md transition duration-200"
          >
            New Customer
          </button>
        )}
      </div>

      {error && <Toast kind="error">{error}</Toast>}

      <section className="bg-white rounded-lg shadow p-4 sm:p-6">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by name (this page)"
            className="w-full sm:w-72 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {pagination && (
            <p className="text-sm text-gray-600">
              {pagination.total} customer{pagination.total === 1 ? '' : 's'} total
            </p>
          )}
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        )}

        {/* Table */}
        {!isLoading && (
          <>
            {visibleCustomers.length === 0 ? (
              <p className="text-gray-600 text-center py-8">
                {term ? 'No customers match this filter' : 'No customers yet'}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="responsive-table min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">City / State</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {visibleCustomers.map((customer) => (
                      <tr
                        key={customer.id}
                        onClick={() => navigate(`/customers/${customer.id}`)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') navigate(`/customers/${customer.id}`);
                        }}
                        tabIndex={0}
                        className="hover:bg-gray-50 cursor-pointer transition focus:outline-none focus:bg-gray-50"
                      >
                        <td data-label="Name" data-primary className="px-4 py-3 font-semibold text-gray-900 break-words">{customer.name}</td>
                        <td data-label="Status" className="px-4 py-3">
                          <CustomerStatusBadge status={customer.status} />
                        </td>
                        <td data-label="Email" className="px-4 py-3 text-sm text-gray-600 break-all sm:break-normal">{customer.email || '—'}</td>
                        <td data-label="Phone" className="px-4 py-3 text-sm text-gray-600">{customer.phone || '—'}</td>
                        <td data-label="City / State" className="px-4 py-3 text-sm text-gray-600">
                          {[customer.city, customer.state].filter(Boolean).join(', ') || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pager */}
            {totalPages > 1 && (
              <div className="flex flex-wrap justify-between items-center gap-2 mt-6">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="py-2.5 sm:py-2 px-4 rounded-md border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <p className="text-sm text-gray-600">
                  Page {page} of {totalPages}
                </p>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="py-2.5 sm:py-2 px-4 rounded-md border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
}
